/**
 * Remove duplicates from an array
 *
 * @template T The type of the array
 * @param data The array to remove duplicates from
 * @returns The array with duplicates removed
 */
export function dedupe(data) {
    return [...new Set(data)];
}
export var Direction;
(function (Direction) {
    Direction[Direction["Left"] = 0] = "Left";
    Direction[Direction["Right"] = 1] = "Right";
})(Direction || (Direction = {}));
/**
 * Get the next index or previous index in array, looping around if necessary.
 *
 * @param currentIndex The current index.
 * @param direction The direction to move in.
 * @param items The array of items.
 * @returns The next index.
 */
export function getLoopedIndex(currentIndex, direction, items) {
    const length = Array.isArray(items) ? items.length : items;
    if (direction === Direction.Left) {
        return currentIndex - 1 < 0 ? length - 1 : currentIndex - 1;
    }
    else {
        return currentIndex + 2 > length ? 0 : currentIndex + 1;
    }
}
/**
 * Reassigns the position field in an array of objects.
 *
 * @example
 * 	const array = [
 * 		{ id: 1, position: 0 },
 * 		{ id: 2, position: 1 },
 * 		{ id: 3, position: 2 },
 * 	];
 * 	const newArray = moveItemInArray(array, "id", 2, 1);
 * 	// newArray is now [{ id: 1, position: 0 }, { id: 3, position: 1 }, { id: 2, position: 2 }];
 *
 * 	const notIntrinsicallyOrderedArray = [
 * 		{ id: 1, position: 0 },
 * 		{ id: 3, position: 2 },
 * 		{ id: 2, position: 1 },
 * 		{ id: 4, position: 3 },
 * 	];
 * 	const newArray2 = moveItemInArray(
 * 		notIntrinsicallyOrderedArray,
 * 		"id",
 * 		2,
 * 		-1,
 * 	);
 * 	// newArray2 is now [{ id: 1, position: 0 }, { id: 2, position: 1 }, { id: 3, position: 2 }, { id: 4, position: 3 }];
 *
 * @template T The type of the objects in the array. The objects must have a
 *   numeric `position` field.
 * @template S The type of the id field.
 * @param array The array of objects.
 * @param idField The field to use as the id.
 * @param id The id of the item to move.
 * @param direction The direction to move the item in. 1 for down, -1 for up.
 * @returns An array with the new position values set.
 */
export function moveItemInArray(array, idField, id, direction) {
    const newArray = [...array];
    const index = newArray.findIndex((item) => item[idField] === id);
    if (index === -1) {
        return newArray;
    }
    const originalPosition = newArray[index].position;
    for (let i = 0; i < newArray.length; i++) {
        if (i === index) {
            if (direction === -1 || i < newArray.length - 1) {
                newArray[i].position += direction;
            }
        }
        else if (direction === 1) {
            if (newArray[i].position > originalPosition &&
                newArray[i].position <= originalPosition + 1) {
                newArray[i].position--;
            }
        }
        else {
            if (newArray[i].position < originalPosition &&
                newArray[i].position >= originalPosition - 1) {
                newArray[i].position++;
            }
        }
    }
    return newArray;
}
/**
 * Mutates the position field in an array of objects to enforce uniqueness.
 *
 * @template T The type of the objects in the array.
 * @param array The array of objects.
 */
export function enforceUniquePositions(array) {
    const newArray = [...array];
    newArray.sort((a, b) => a.position - b.position);
    for (let i = 0; i < newArray.length; i++) {
        newArray[i].position = i;
    }
}
